import React, { useEffect, useRef, useState } from 'react';
import { Form, OverlayTrigger, Popover, PopoverContent, PopoverTitle, Spinner as Spin, Breadcrumb } from 'react-bootstrap';
import ProductDescriptionDetails from '../Cards/ProductDescriptionDetails';
import _ from 'lodash';
import { FoundProduct, Product, ProductsGroup } from 'lib/types';
import TreeSelect from './TreeSelect';
import { TreeNode } from 'lib/tree';
import { AssortmentSelectionState, firstNGroups, pathToArray } from './AssortmentSelection';
import { useSelector, useDispatch } from 'react-redux';
import { AppState, dataActions } from 'reducers';
import './AssortmentSelection.scss';
import { FaArrowLeft } from 'react-icons/fa';

import ButtonComponent from 'components/ButtonComponent';
import { AiOutlineInfo } from 'react-icons/ai';
import useDebounce from 'lib/hooks/useDebounce';
import { searchProducts } from 'lib/communication/orders';
import ListSelect from './ListSelect';
import { useHistory } from 'react-router-dom';
import Spinner from 'components/Spinner';
import Toggle from 'components/Toggle';
import { useDevice } from 'lib/hooks/useDevice';

interface AdvancedViewProps {
    error: boolean;
    tree: TreeNode[] | null;
    findInTree: string;
    selectedPath: string;
    selectedProduct: Product | null;
    selectProduct: (symkar: string, unit: string) => Promise<void>;
    setFindInTree: (_: string) => void;
    toggleTreeNode: (key: string) => void;
    expandAllTreeNodeParents: (key: string) => void;
    onUpdate: (productSymKar: string, path: string[], unit: string) => void;
    focusFirstInput: () => void;
    isLoading: boolean;
}

const AdvancedView = ({
    error, tree, findInTree, selectedProduct, selectedPath,
    setFindInTree, toggleTreeNode, expandAllTreeNodeParents, onUpdate, focusFirstInput, isLoading
}: AdvancedViewProps) => {
    const dispatch = useDispatch();

    const { isDesktop } = useDevice();
    const groups = useSelector((state: AppState) => state.data.orders.groupsAndProducts.groups);
    const advancedTree = useSelector((state: AppState) => state.data.orders.advancedTree);
    const isProductDetailsFetching = useSelector((state: AppState) => state.data.orders.props.isProductDetailsFetching);

    const [showTree, setShowTree] = useState<Boolean>(false);

    const [searchMode, setSearchMode] = useState<string>('products');
    const [findProduct, setFindProduct] = useState<string>('');
    const [foundProducts, setFoundProducts] = useState<FoundProduct[]>([]);
    const [productsListMessage, setProductsListMessage] = useState<string | null>(null);
    const [findInTreeBackup, setFindInTreeBackup] = useState<string>('');
    const [isKeyboardAdvView, setIsKeyboardAdvView] = useState<boolean>(false);

    const debouncedFindProduct = useDebounce(findProduct);

    const history = useHistory<AssortmentSelectionState>();

    const groupsInputRef = useRef<HTMLInputElement | null>(null);
    const productsInputRef = useRef<HTMLInputElement | null>(null);
    const groupsButtonRef = useRef<HTMLInputElement | null>(null);
    const productsButtonRef = useRef<HTMLInputElement | null>(null);

    const treeRef = useRef<HTMLDivElement | null>(null);

    const [nodeToScrollToKey, setNodeToScrollToKey] = useState<string | null>(null);

    const searchProductsByName = async () => {
        if (debouncedFindProduct.length === 0) {
            setFoundProducts([]);
            setProductsListMessage('Wprowadź nazwę produktu');
        } else {
            setProductsListMessage(null);
            const products = await searchProducts(debouncedFindProduct);
            if (products) {
                setProductsListMessage(null);
                setFoundProducts(products);
            } else {
                setProductsListMessage('Błąd pobierania listy produktów.');
            }
        }
    };

    const removeItems = (arr: ProductsGroup[], values: ProductsGroup[]) => {
        values.forEach((value) => {
            const index = arr.indexOf(value);
            if (index > -1) {
                arr.splice(index, 1);
            }
        });
    };

    const findNodeChildren = (levelIndex: number, parentPath: string, restGroups: ProductsGroup[]): TreeNode[] => {
        const paths = _.uniq(
            _.map(
                _.filter(restGroups, (group) => group.hasProducts && parentPath === firstNGroups(group.path, levelIndex + 1)),
                (group) => firstNGroups(group.path, levelIndex + 2)
            )
        );
        const children: ProductsGroup[] = _.filter(restGroups, (group) => _.some(paths, (path) => path === group.path));
        const filteredByLevel = _.filter(children, (group) => group.level === levelIndex + 2);

        removeItems(restGroups, filteredByLevel);
        return filteredByLevel.length ? _.map(filteredByLevel, (child) => ({
            ...child,
            key: `${child.path}`,
            expanded: false,
            childrenNodes: findNodeChildren(levelIndex + 1, child.path, restGroups)
        })) : [];
    };

    const createAdvancedTree = () => {
        // Get all root paths that have products
        const paths = _.uniq(
            _.map(
                _.filter(groups, (group) => group.hasProducts),
                (group) => firstNGroups(group.path, 2)
            )
        );

        const groupArray = [...groups];
        const rootGroups: ProductsGroup[] = _.filter(groups, (group) => _.some(paths, (path) => path === group.path));

        removeItems(groupArray, rootGroups);
        // Map children for each root node
        const t = _.map(rootGroups, (group, index) => ({
            ...group,
            key: `${group.path}`,
            expanded: false,
            childrenNodes: findNodeChildren(1, group.path, groupArray)
        }));
        dispatch(dataActions.setAdvancedTree(t));
    };

    // disable tab indexing on groups and products buttons
    useEffect(() => {
        if (groupsButtonRef.current?.firstElementChild?.tagName.toLowerCase() === 'input') {
            (groupsButtonRef.current.firstElementChild as HTMLElement).tabIndex = -1;
        }
        if (productsButtonRef.current?.firstElementChild?.tagName.toLowerCase() === 'input') {
            (productsButtonRef.current.firstElementChild as HTMLElement).tabIndex = -1;
        }
    }, []);

    useEffect(() => {
        if (!advancedTree && groups) {
            createAdvancedTree();
        }
    }, [dispatch, groups, advancedTree]);

    useEffect(() => {
        searchProductsByName();
    }, [debouncedFindProduct]);

    const renderTooltip = () => (
        <OverlayTrigger
            key='right'
            placement='right'
            overlay={
                <Popover id='popover-positioned-right'>
                    <PopoverTitle as='h3'>Nawigacja klawiaturą</PopoverTitle>
                    <PopoverTitle as='h3'>Pole wyszukiwania</PopoverTitle>
                    <PopoverContent>
                        <div className='key-group'>
                            <div className='key-box'>A - Ż</div>
                            ,
                            <div className='key-box'>0 - 9</div>
                            ,
                            <div className='key-box'>Space</div>
                        </div>
                        <div className='key-description'>Wpisywanie w polu wyszukiwania.</div>
                        <div className='key-group'><div className='key-box'>Tab</div></div>
                        {' '}
                        <div className='key-description'>Przejście do nawigacji po drzewie.</div>
                    </PopoverContent>
                    <PopoverTitle as='h3'>Drzewo</PopoverTitle>
                    <PopoverContent>
                        <div className='key-group'>
                            <div className='key-box'>↑</div>
                            ,
                            {' '}
                            <div className='key-box'>↓</div>
                        </div>
                        {' '}
                        <div className='key-description'>Poruszanie się w górę i w dół drzewa.</div>
                        <div className='key-group'>
                            <div className='key-box'>Shift + Tab</div>
                            ,
                            <div className='key-box'>Tab</div>
                        </div>
                        {' '}
                        <div className='key-description'>Poruszanie się w górę i w dół drzewa.</div>
                        <div className='key-group'><div className='key-box'>→</div></div>
                        {' '}
                        <div className='key-description'>Rozwijanie gałęzi lub wybranie produktu z przejściem do pola parametrów produktu.</div>
                        <div className='key-group'><div className='key-box'>←</div></div>
                        {' '}
                        <div className='key-description'>Zwinięcie gałęzi.</div>
                        <div className='key-group'><div className='key-box'>Enter</div></div>
                        {' '}
                        <div className='key-description'>Wybranie produktu z przejściem do pola parametrów produktu.</div>
                        <div className='key-group'><div className='key-box'>Backspace</div></div>
                        {' '}
                        <div className='key-description'>Powrót do pola wpisywania nazwy.</div>
                    </PopoverContent>
                    <PopoverTitle as='h3'>Pole parametrów produktu</PopoverTitle>
                    <PopoverContent>
                        <div className='key-group'>
                            <div className='key-box'>↑</div>
                            ,
                            {' '}
                            <div className='key-box'>↓</div>
                        </div>
                        {' '}
                        <div className='key-description'>Zwiększanie i zmniejszanie wartości pól numerycznych.</div>
                        <div className='key-group'>
                            <div className='key-box'>Shift + Tab</div>
                            ,
                            <div className='key-box'>Tab</div>
                        </div>
                        {' '}
                        <div className='key-description'>Przeskakiwanie do poprzedniego lub następnego pola zaznaczania/wpisywania.</div>
                        <div className='key-group'><div className='key-box'>Space</div></div>
                        {' '}
                        <div className='key-description'>Zmienianie wartości pól typu check-box.</div>
                        <div className='key-group'><div className='key-box'>Enter</div></div>
                        {' '}
                        <div className='key-description'>Dodawanie produktu do koszyka z wybranymi parametrami (Ilość, Wymiar i Folia).</div>
                    </PopoverContent>
                </Popover>
            }
        >
            <div className='navigation-info key-box'><AiOutlineInfo size='20' /></div>
        </OverlayTrigger>
    );

    const switchMode = () => {
        const mode = searchMode;
        if (mode === 'products') {
            setFindInTreeBackup(findInTree);
            setFindInTree('');
            setSearchMode('groups');
        } else if (mode === 'groups') {
            setFindInTree(findInTreeBackup);
            setSearchMode('products');
        }
    };

    const renderFormControl = () => (
        searchMode === 'groups' ? <Form.Control
            id='searchGroups'
            type='text'
            placeholder='Wyszukaj grupę'
            value={findInTree}
            onChange={(event) => setFindInTree(event.target.value)}
            ref={groupsInputRef}
        /> : <Form.Control
            id='searchProducts'
            type='text'
            placeholder='Wyszukaj produkt'
            value={findProduct}
            onChange={(event) => setFindProduct(event.target.value)}
            ref={productsInputRef}
        />
    );

    const renderBreadcrumb = () => {
        const onClick = (index: number) => {
            const path = firstNGroups(selectedPath, index + 1);
            history.push({
                pathname: `${process.env.PUBLIC_URL}/new-order`,
                state: {
                    ...history.location.state,
                    selectedPath: path,
                    selectedView: 'advanced',
                    selectedProduct: null
                }
            });
            expandAllTreeNodeParents(path);
            setNodeToScrollToKey(path);
        };

        const renderBreadcrumbLabel = (index: number) => (
            index === 0 ? 'Asortyment' :
            _.find(groups, (g) => firstNGroups(selectedPath, index + 1) === g.path)?.description
        );

        return (
            <Breadcrumb className='breadcrumb-advanced-view'>
                {_.map(pathToArray(selectedPath), (__, index) => (
                    <Breadcrumb.Item
                        active={index === pathToArray(selectedPath).length - 1 && selectedProduct === null}
                        key={`bredcrumb${index}`}
                        onClick={() => onClick(index)}
                    >
                        {renderBreadcrumbLabel(index)}
                    </Breadcrumb.Item>
                ))}
            </Breadcrumb>
        );
    };

    if (isLoading) return <div style={{ gridColumn: 'span 2' }}><Spinner centered /></div>;

    const renderSearchbar = () => (
        (isDesktop ?
            <div
                className='AssortmentSelection-SearchBar'
                onKeyDown={() => setIsKeyboardAdvView(true)}
                onMouseDown={() => setIsKeyboardAdvView(false)}
            >
                {renderFormControl()}
                <Toggle
                    leftLabel='GRUPY'
                    rightLabel='PRODUKTY'
                    toggleState={searchMode === 'groups' ? 'left' : 'right'}
                    onToggle={switchMode}
                    mode={'desktop'}
                    size='compact'
                />
                <div className='AssortmentSelection-navigation-info'>{renderTooltip()}</div>
            </div> :

            <div className='AssortmentSelection-SearchBar'>
                <Toggle
                    leftLabel='GRUPY'
                    rightLabel='PRODUKTY'
                    toggleState={searchMode === 'groups' ? 'left' : 'right'}
                    onToggle={switchMode}
                    mode='mobile'
                    size='compact'
                />
                {renderFormControl()}
            </div>)
    );

    const renderProductTree = () => {
        const treeSelect = searchMode === 'groups' || (searchMode === 'products' && findProduct.length === 0);

        if (treeSelect && !isDesktop && !tree) {
            return <Spin animation='border' />;
        }

        return (
            <div
                className='AssortmentSelection-tree'
                ref={treeRef}
                onKeyDown={isDesktop ? () => setIsKeyboardAdvView(true) : undefined}
                onMouseDown={isDesktop ? () => setIsKeyboardAdvView(false) : undefined}
            >
                {treeSelect ? (
                    <TreeSelect
                        error={error}
                        tree={tree}
                        selectedPath={selectedPath}
                        selectedProduct={selectedProduct}
                        isKeyboardAdvView={isDesktop ? isKeyboardAdvView : false}
                        toggleTreeNode={toggleTreeNode}
                        onUpdate={onUpdate}
                        focusFirstInput={focusFirstInput}
                        treeRef={treeRef}
                        nodeToScrollToKey={nodeToScrollToKey}
                        textInputRef={searchMode === 'groups' ? groupsInputRef : productsInputRef}
                        setShowTree={isDesktop ? undefined : setShowTree}
                    />
                ) : (
                    <ListSelect
                        message={productsListMessage}
                        products={foundProducts}
                        setShowTree={isDesktop ? undefined : setShowTree}
                    />
                )}
            </div>
        );
    };

    const renderDetails = () => {
        const isSomethingSelected = selectedProduct || isProductDetailsFetching;
        return (isSomethingSelected ? (
            <div
                className='AssortmentSelection-AdvancedView-ProductDescription'
                onKeyDown={() => setIsKeyboardAdvView(true)}
                onMouseDown={() => setIsKeyboardAdvView(false)}
            >
                <ProductDescriptionDetails selectedProduct={selectedProduct} />
            </div>
        ) : (
            <div
                className='AssortmentSelection-AdvancedView-Title'
                onKeyDown={() => setIsKeyboardAdvView(true)}
                onMouseDown={() => setIsKeyboardAdvView(false)}
            >
                <h2>Wybierz produkt z listy</h2>
            </div>
        ));
    };

    const renderDesktopView = () => (
        <>
            {renderSearchbar()}
            <div className='AssortmentSelection-AdvancedView-breadcrumb'>
                {renderBreadcrumb()}
            </div>
            {renderProductTree()}
            {renderDetails()}
        </>
    );

    const renderMobileView = () => (
        <div>
            {isProductDetailsFetching ? (
                <Spin animation='border' />
            ) : showTree || !selectedProduct ? (
                <>
                    <h2>Wybierz produkt z listy</h2>
                    <div className='NewOrder-leftBar'>
                        {renderSearchbar()}
                        {renderProductTree()}
                    </div>
                </>
            ) : (
                <>
                    <ButtonComponent
                        isIconOnTheRight={false}
                        icon={<FaArrowLeft />}
                        text='WRÓĆ DO WYSZUKIWANIA'
                        onClick={() => setShowTree(true)}
                        marginTop='0px'
                    />
                    {renderBreadcrumb()}
                    <ProductDescriptionDetails selectedProduct={selectedProduct} />
                </>
            )}
        </div>
    );

    return isDesktop ?
        renderDesktopView() :
        renderMobileView();
};

export default AdvancedView;
